import { format, isValid, parse } from 'date-fns';
import type { DateFormat, TimeFormat } from '@timato/shared';
import { useSettings } from '@/data/hooks';

/**
 * Clock times and calendar dates follow a setting, never the browser's locale: a European on an en-US browser
 * otherwise gets 12-hour pickers and month-first dates with no way out. One table per kind, used for display,
 * for parsing what people type, and for the labels on the Appearance screen.
 */
export const TIME_FORMATS: Record<TimeFormat, { label: string; pattern: string }> = {
  '24h': { label: '24-hour', pattern: 'HH:mm' },
  '12h': { label: '12-hour', pattern: 'h:mm aaa' },
};
export const DATE_FORMATS: Record<DateFormat, { label: string; pattern: string; placeholder: string }> = {
  dmy: { label: 'Day / month / year', pattern: 'dd/MM/yyyy', placeholder: 'DD/MM/YYYY' },
  mdy: { label: 'Month / day / year', pattern: 'MM/dd/yyyy', placeholder: 'MM/DD/YYYY' },
  ymd: { label: 'Year-month-day', pattern: 'yyyy-MM-dd', placeholder: 'YYYY-MM-DD' },
};

/** Canonical forms the rest of the app stores and computes with; only the edges format and parse. */
export const HM = 'HH:mm';
export const YMD = 'yyyy-MM-dd';

export const formatTime = (d: Date, tf: TimeFormat) => format(d, TIME_FORMATS[tf].pattern);
export const formatDate = (d: Date, df: DateFormat) => format(d, DATE_FORMATS[df].pattern);

/**
 * "9", "9:5", "0905", "21:05", "9pm", "9:05 PM", "9.05" → "HH:mm", or null. Lenient on purpose: a time field is
 * typed into quickly, and the 12-hour marker wins over the setting when someone writes one.
 */
export function parseTime(text: string, tf: TimeFormat): string | null {
  const s = text.trim().toLowerCase().replace(/\s+/g, '');
  const m = s.match(/^(\d{1,2})(?:[:.h]?(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const marker = m[3]?.[0];
  if (min > 59) return null;
  if (marker) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (marker === 'p' ? 12 : 0);
  } else if (tf === '12h' && s.length <= 2 && h >= 1 && h <= 12 && !m[2]) {
    // A bare "9" in 12-hour mode is ambiguous; read it as the morning, the same as the picker would.
  }
  if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** The chosen pattern, with "/", "." or "-" between the parts, or ISO year-first, which is never ambiguous. → "yyyy-MM-dd" or null. */
export function parseDate(text: string, df: DateFormat): string | null {
  const s = text.trim().replace(/[.\-\s]/g, '/');
  for (const pattern of [DATE_FORMATS[df].pattern.replace(/-/g, '/'), 'yyyy/MM/dd']) {
    const d = parse(s, pattern, new Date());
    if (isValid(d) && d.getFullYear() > 1900) return format(d, YMD);
  }
  return null;
}

/** The settings-driven formatters for a component: `time(date)`, `date(date)`, and the raw choices for fields. */
export function useFormats() {
  const { timeFormat, dateFormat } = useSettings();
  return {
    timeFormat, dateFormat,
    time: (d: Date | string) => formatTime(typeof d === 'string' ? new Date(d) : d, timeFormat),
    date: (d: Date | string) => formatDate(typeof d === 'string' ? new Date(d) : d, dateFormat),
  };
}
